import { create } from "zustand";
import type { ImportLegalBasisConfirmation } from "@/lib/legal/importLegalBasis";

/**
 * Bridge between the AI assistant (mounted ABOVE pages in EventLayoutShell)
 * and page-local UI state (plan §4.5.3). React context can't flow sideways,
 * so pages register named CLIENT-tool handlers here on mount and the
 * assistant provider executes them via `getState()` when the backend emits a
 * `client_tool` SSE frame.
 *
 * Security invariant (plan §4.5.1): a handler must only mutate UI state the
 * user's own page controls expose (apply a filter, switch a view) — anything
 * touching data goes through the SERVER tool lane with the ephemeral PAT.
 *
 * Handlers RETURN a JSON-serializable result (including soft validation
 * errors like `{ok:false, errorCode:"unknownField", validKeys:[…]}` — the
 * model self-corrects from them). THROWING is reserved for hard failures;
 * the provider maps a thrown `Error.message` to the re-entry `errorCode`.
 */
export type AiClientToolHandler = (
  input: unknown,
  confirmation?: AiClientConfirmation,
) => Promise<unknown> | unknown;

/**
 * What the ConfirmationCard hands a confirm-gated client tool beside its
 * input when the user approved it (Welle Z W8). Today only the spreadsheet
 * import uses it: the organiser's legal-basis tick, built by the CARD from
 * its own constants and the UI locale. It travels onResolve →
 * confirmPendingTool → executeClientTool → handler(input, confirmation) and
 * is NEVER parsed from the tool input the model wrote — a handler that needs
 * the tick reads it here, never from `input`. The auto-run lane and an
 * approve without it pass nothing.
 */
export type AiClientConfirmation = ImportLegalBasisConfirmation;

/**
 * Confirm-gate declaration for a client tool: `true` always gates, a
 * predicate gates per-call from the parsed tool input. Gated calls pause on
 * the same ConfirmationCard as server confirm tools instead of auto-running.
 */
export type AiClientToolConfirm = boolean | ((input: unknown) => boolean);

type RegisterOptions = {
  requiresConfirm?: AiClientToolConfirm;
};

type AiToolBridgeState = {
  handlers: Record<string, AiClientToolHandler>;
  confirmGates: Record<string, AiClientToolConfirm>;
  /** Idempotent per name; last mount wins (only one guestlist page is open). */
  register: (name: string, handler: AiClientToolHandler, options?: RegisterOptions) => void;
  /**
   * Removes the handler ONLY if `handler` is still the registered one —
   * an unmount racing a newer page's mount must not strip the new handler.
   */
  unregister: (name: string, handler: AiClientToolHandler) => void;
};

export const useAiToolBridge = create<AiToolBridgeState>((set) => ({
  handlers: {},
  confirmGates: {},
  register: (name, handler, options) =>
    set((s) => {
      if (import.meta.env.DEV && s.handlers[name] && s.handlers[name] !== handler) {
        console.warn(
          `ai-tool-bridge: handler "${name}" re-registered by another component — the previous handler is dropped (last mount wins)`,
        );
      }
      const confirmGates = { ...s.confirmGates };
      if (options?.requiresConfirm !== undefined) {
        confirmGates[name] = options.requiresConfirm;
      } else {
        delete confirmGates[name];
      }
      return { handlers: { ...s.handlers, [name]: handler }, confirmGates };
    }),
  unregister: (name, handler) =>
    set((s) => {
      if (s.handlers[name] !== handler) return s;
      const next = { ...s.handlers };
      delete next[name];
      const confirmGates = { ...s.confirmGates };
      delete confirmGates[name];
      return { handlers: next, confirmGates };
    }),
}));
